import { component } from 'picoapp';
import { addToCart } from '~/js/lib/cart-helpers';
import basketIcon from '~/js/utils/basket-icon';
import { sortVariantOptions } from '~/js/lib/sort-option-values';

export default component(async (node) => {
  sortVariantOptions(node);

  let currentProduct = null;

  /**
   * Get product data from JSON script tag
   */
  const getProductData = () => {
    const script = node.querySelector('[data-product-data]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse product data:', error);
      return null;
    }
  };

  /**
   * Update form with new variant data
   */
  const updateVariant = (variant) => {
    if (!variant) return;

    // Update hidden variant ID input
    const variantInput = node.querySelector('[data-variant-id]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update price display
    const priceDisplay = node.querySelector('[data-product-price]');
    if (priceDisplay) {
      const formattedPrice = new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
      }).format(variant.price / 100);
      priceDisplay.textContent = formattedPrice;
    }

    // Update add to cart button using the same pattern as product-actions.js
    const addToCartBtn = node.querySelector('[data-add-to-cart-btn]');
    if (addToCartBtn) {
      if (variant.available) {
        addToCartBtn.innerHTML = `${basketIcon()} ${window.theme?.strings?.addToCart || 'Add to cart'}`;
        addToCartBtn.removeAttribute('disabled');
      } else {
        addToCartBtn.innerHTML = window.theme?.strings?.soldOut || 'Sold out';
        addToCartBtn.setAttribute('disabled', 'disabled');
      }
    }
  };

  /**
   * Handle option selector changes - works with select dropdowns
   */
  const handleOptionChange = () => {
    // Get all visible option selectors (not the hidden variant selector)
    const optionSelectors = node.querySelectorAll('[data-option-selector]:not(.hidden)');
    const selectedOptions = {};

    // Build selected options object
    optionSelectors.forEach((selector) => {
      const { optionIndex, optionName } = selector.dataset;
      if (optionIndex) {
        selectedOptions[`option${optionIndex}`] = selector.value;
      } else if (optionName) {
        // Fallback based on option name
        let optionNumber;
        if (optionName === 'Size') {
          optionNumber = '1';
        } else if (optionName === 'Color') {
          optionNumber = '2';
        } else {
          optionNumber = '3';
        }
        selectedOptions[`option${optionNumber}`] = selector.value;
      }
    });

    // Find matching variant
    const matchingVariant = currentProduct.variants.find((variant) => Object.keys(selectedOptions).every((optionKey) => variant[optionKey] === selectedOptions[optionKey]));

    if (matchingVariant) {
      // Update hidden variant selector
      const hiddenSelector = node.querySelector(`#product-${currentProduct.id}`);
      if (hiddenSelector) {
        hiddenSelector.value = matchingVariant.id;
      }

      // Update variant info
      updateVariant(matchingVariant);
    }
  };

  /**
   * Handle form submission - matches pattern from product-actions.js
   */
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);
    const addToCartBtn = form.querySelector('[data-add-to-cart-btn]');

    if (!addToCartBtn) return;

    // Disable button and show loading state
    addToCartBtn.style.pointerEvents = 'none';
    addToCartBtn.textContent = window.theme?.strings?.adding || 'Adding...';

    // Note: WishlistKing integration removed for shop-the-look forms

    // Use the same addToCart pattern as product-actions.js
    addToCart(formData, () => {
      if (addToCartBtn) {
        addToCartBtn.textContent = window.theme?.strings?.added || 'Added';
        addToCartBtn.insertAdjacentHTML('afterbegin', window.theme?.icons?.check || '✓');

        setTimeout(() => {
          addToCartBtn.textContent = window.theme?.strings?.addToCart || 'Add to cart';
          addToCartBtn.insertAdjacentHTML('afterbegin', window.theme?.icons?.cart || basketIcon());
          addToCartBtn.style.pointerEvents = 'auto';
        }, 4000);
      }
    });
  };

  /**
   * Initialize form event listeners - follows pattern from product-actions.js
   */
  const initEventListeners = () => {
    // Check if the current node (form) itself has data-action="submit"
    if (node.dataset.action === 'submit') {
      node.addEventListener('submit', handleFormSubmit);
    }

    // Also handle other actions inside the form (like modal buttons)
    const actions = [...node.querySelectorAll('[data-action]')];

    if (actions.length) {
      actions.forEach((el) => {
        const { action } = el.dataset;

        switch (action) {
          case 'open-product-modal':
            el.addEventListener('click', (e) => {
              e.preventDefault();
              const { productHandle } = el.dataset;

              const event = new CustomEvent('stl:modal:open', {
                detail: { handle: productHandle },
              });
              document.dispatchEvent(event);
            });
            break;
          case 'open-size-guide':
            el.addEventListener('click', (e) => {
              e.preventDefault();
              const { productHandle, optionName } = el.dataset;

              const event = new CustomEvent('stl:size-guide:open', {
                detail: {
                  productHandle,
                  optionName,
                },
              });
              document.dispatchEvent(event);
            });
            break;
          default:
            break;
        }
      });
    }

    // Handle option changes - attach to visible option selectors only
    const optionSelectors = node.querySelectorAll('[data-option-selector]:not(.hidden)');
    optionSelectors.forEach((selector) => {
      selector.addEventListener('change', handleOptionChange);
    });
  };

  /**
   * Initialize component
   */
  const init = () => {
    currentProduct = getProductData();

    if (!currentProduct) {
      // eslint-disable-next-line no-console
      console.error('No product data found for STL form');
      return;
    }

    initEventListeners();

    // Set initial variant
    if (currentProduct.variants?.length > 0) {
      updateVariant(currentProduct.variants[0]);
    }
  };

  /**
   * Handle product change from thumbnail clicks
   */
  const handleProductChange = async () => {
    // This will be handled by the fetching component
    // We just need to wait for the DOM to update and reinitialize
    setTimeout(() => {
      init();
    }, 100);
  };

  // Listen for product changes
  document.addEventListener('stl:product:change', handleProductChange);

  // Initialize
  init();

  // Cleanup
  return () => {
    document.removeEventListener('stl:product:change', handleProductChange);
  };
});
